import {
  DestroyRef,
  Injectable,
  computed,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, map } from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';

import { AssistantProviderId } from '@core/models/assistant/assistant-settings.model';
import {
  AssistantApprovalCard,
  AssistantApprovalMode,
  AssistantApprovalState,
  AssistantChatRequest,
  AssistantConfig,
  AssistantConnection,
  AssistantErrorCode,
  AssistantErrorData,
  AssistantEvent,
  AssistantFile,
  AssistantMessage,
  AssistantPlanEdit,
  AssistantTab,
  PageContextV1
} from '@core/models/assistant/assistant.model';
import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import { AssistantPlanHold } from '@core/models/plan-edit.model';
import { UserPermissionName } from '@core/models/user.model';
import { FeatureFlagsFacade } from '@core/services/feature-flags/feature-flags.facade';
import { GbeTokensService } from '@core/services/gbe-tokens/gbe-tokens.service';
import { PlansService } from '@core/services/plans/plans.service';
import { VisualFocusService } from '@core/services/visual-focus/visual-focus.service';
import { selectUser, selectUserPermissionNames } from '@core/store/auth/auth.selectors';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';
import { PlanEditSessionActions as Session } from '@core/store/plan-edit/plan-edit-session.actions';
import { selectPlanEditSession } from '@core/store/plan-edit/plan-edit-session.selectors';
import { PlanEditActions } from '@core/store/plan-edit/plan-edit.actions';
import * as PlanActions from '@core/store/plan/plan.actions';
import { selectPlan, selectPlanState } from '@core/store/plan/plan.selectors';
import {
  buildPageContext,
  describePageContext
} from '@core/utils/assistant-page-context.utils';
import { extractDataspaceIdFromUrl } from '@core/utils/dataspace-url.utils';

import { AssistantApiService, AssistantConfigError } from './assistant-api.service';
import { interruptStreaming, withoutFileContents } from './assistant-conversation-store';
import { AssistantRunsService } from './assistant-runs.service';
import { AssistantTableStateService } from './assistant-table-state.service';
import { AssistantTabsSync, SyncChange } from './assistant-tabs-sync';
import { AssistantWindowLink } from './assistant-window-link';

const ASSISTANT_PERMISSIONS: readonly UserPermissionName[] = [
  'ai_assistant_read',
  'ai_assistant_write',
  'ai_assistant_plan'
];
// What the server's ChatRequestSchema accepts for `lang`.
const LANG_TAG = /^[a-z]{2,3}(-[A-Za-z]{2,4})?$/;
const KEY_PREFIX = 'gfe.assistant.key.';
// Saving serialises every tab, so a streamed reply is saved once it pauses rather than per token.
const SAVE_DELAY_MS = 500;
// The server rejects tokens with under a minute left and ends a turn 10s before its token expires, so
// a token reused from storage must outlast a whole turn (4 minutes by default).
const MIN_TOKEN_TTL_MS = 5 * 60_000;
const NO_TOKEN: AssistantErrorData = {
  code: 'unauthenticated',
  message: 'Your session has expired. Sign in again.',
  retryable: false
};
const NETWORK: AssistantErrorData = {
  code: 'internal',
  message: 'The assistant server is unreachable.',
  retryable: true
};

const CONTEXT_NOTICES = new Map<string, AssistantTab['contextNotice']>([
  ['org_context_not_applied', 'not_applied'],
  ['org_context_unchecked', 'unchecked']
]);

// 'kept': the request ended without the server touching the conversation, so it still waits as it was.
type Outcome = 'done' | 'kept' | 'unauthenticated' | 'conversation_lost';

export type PanelSize = 'thin' | 'large' | 'focus';

interface Reply {
  tabId: string;
  replyId: string;
}

interface Scope {
  userName: string;
  dataSpaceId: number;
}

@Injectable({ providedIn: 'root' })
export class AssistantService {
  private readonly api = inject(AssistantApiService);
  private readonly tokens = inject(GbeTokensService);
  private readonly router = inject(Router);
  private readonly visualFocus = inject(VisualFocusService);
  private readonly tableState = inject(AssistantTableStateService);
  private readonly flags = inject(FeatureFlagsFacade);
  private readonly runs = inject(AssistantRunsService);
  private readonly plans = inject(PlansService);
  private readonly sync = inject(AssistantTabsSync);
  private readonly link = inject(AssistantWindowLink);
  private readonly translate = inject(TranslateService);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );
  private readonly flagActive = this.flags.isActive(
    KNOWN_FEATURE_FLAGS.ENABLE_AI_ASSISTANT
  );

  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly editSession = this.store.selectSignal(selectPlanEditSession);
  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly permissions = this.store.selectSignal(selectUserPermissionNames);
  private readonly user = this.store.selectSignal(selectUser);
  private readonly userName = computed(() => this.user()?.userName ?? null);
  private readonly planState = this.store.selectSignal(selectPlanState);
  private readonly hasAssistantPermission = computed(() =>
    this.permissions().some((p) => ASSISTANT_PERMISSIONS.includes(p))
  );

  readonly dataSpaceId = computed(() => {
    const id = extractDataspaceIdFromUrl(this.url());
    return id !== null && /^\d+$/.test(id) && Number(id) > 0 ? Number(id) : null;
  });
  /** The linked tab's page, only while the tab is in this window's data space. */
  private readonly linkedPage = computed(() => {
    const page = this.link.role() === 'child' ? this.link.linked() : null;
    return page && page.dataSpaceId === this.dataSpaceId() ? page : null;
  });
  readonly pageContext = computed<PageContextV1 | null>(() => {
    const dataSpaceId = this.dataSpaceId();
    if (dataSpaceId === null) return null;
    if (this.link.role() === 'child')
      return (
        this.linkedPage()?.pageContext ?? {
          v: 1,
          page: 'dataspace',
          data_space_id: dataSpaceId
        }
      );
    return buildPageContext(
      dataSpaceId,
      this.url(),
      this.visualFocus.pinnedRouteUid(),
      this.visualFocus.knownRouteUids(),
      this.tableState.state()
    );
  });
  readonly contextSummary = computed(() => {
    const context = this.pageContext();
    if (!context) return null;
    // The window has no plan of its own in the store, so the tab's summary names the plan.
    const linked = this.linkedPage()?.summary;
    if (linked) return linked;
    const plan = this.planState()?.plan;
    return describePageContext(context, plan ? { id: plan.id, name: plan.name } : null);
  });
  private readonly currentDataSpace = this.store.selectSignal(selectDataspaceModel);
  /** Another business unit's data space: the server may still offer the assistant from the user's own. */
  private readonly visiting = computed(() => {
    const dataSpace = this.currentDataSpace();
    const own = this.user()?.businessUnit?.id;
    return (
      dataSpace !== undefined &&
      dataSpace.id === this.dataSpaceId() &&
      dataSpace.businessUnitId !== undefined &&
      own !== undefined &&
      dataSpace.businessUnitId !== own
    );
  });
  /** Whether toggleVisible is final: false before then may only mean the flags or config are loading. */
  readonly accessKnown = computed(() => {
    if (!this.hasAssistantPermission() || this.dataSpaceId() === null) return true;
    return this.visiting()
      ? this.config() !== null || this.configError() !== null
      : this.flags.loaded();
  });
  readonly toggleVisible = computed(() => {
    if (!this.hasAssistantPermission() || this.dataSpaceId() === null) return false;
    // The client's flag says nothing about a visitor: the server decides from the user's own data space.
    if (this.visiting())
      return this.config()?.access.use === true || this.configError() !== null;
    return this.flagActive();
  });
  readonly panelOpen = signal(false);
  readonly panelSize = signal<PanelSize>('thin');
  private sizeBeforeFocus: 'thin' | 'large' = 'thin';
  readonly presentation = computed<'side' | 'focus' | 'window'>(() =>
    this.link.role() === 'child'
      ? 'window'
      : this.panelSize() === 'focus'
        ? 'focus'
        : 'side'
  );
  readonly previewFile = signal<AssistantFile | null>(null);
  readonly config = signal<AssistantConfig | null>(null);
  readonly configError = signal<AssistantErrorCode | null>(null);
  readonly access = computed(() => ({
    use: this.toggleVisible() && (this.config()?.access.use ?? false)
  }));
  readonly level = computed(() => this.config()?.access.level ?? null);
  readonly homeDataSpaceId = computed(
    () => this.config()?.access.home_data_space_id ?? null
  );
  readonly connections = computed(() => this.config()?.connections ?? []);
  readonly notConfigured = computed<'admin' | 'user' | null>(() => {
    const config = this.config();
    if (!config?.access.use || config.connections.length > 0) return null;
    return config.access.manage_settings ? 'admin' : 'user';
  });
  private readonly tabList = signal<AssistantTab[]>([emptyTab(null, null)]);
  readonly tabs = this.tabList.asReadonly();
  private readonly activeId = signal(this.tabList()[0].id);
  readonly activeTabId = this.activeId.asReadonly();
  readonly draft = signal('');
  readonly busyTabIds = this.sync.busyElsewhere;
  readonly busyElsewhere = computed(() => this.busyTabIds().has(this.activeId()));
  private readonly activeTab = computed(
    () => this.tabList().find((t) => t.id === this.activeId()) ?? this.tabList()[0]
  );
  private readonly connectionId = computed(() => this.activeTab().connectionId);
  readonly selectedConnection = computed<AssistantConnection | null>(
    () => this.connections().find((c) => c.id === this.connectionId()) ?? null
  );
  readonly model = computed(() => this.activeTab().model);
  readonly messages = computed(() => this.activeTab().messages);
  readonly contextNotice = computed(() => this.activeTab().contextNotice);
  readonly approvalMode = computed(() => this.activeTab().approvalMode);
  readonly hasPendingApprovals = computed(() =>
    this.messages().some((m) => m.approvals?.some((c) => c.state === 'pending'))
  );
  readonly streaming = computed(() => this.activeTab().streaming);
  /** A reply streams in some conversation, not only the open one: leaving this window would cut it. */
  readonly anyStreaming = computed(() => this.tabList().some((t) => t.streaming));
  private readonly planHolds = computed<AssistantPlanHold[]>(
    () =>
      this.tabList().flatMap((t) =>
        (t.planEdits ?? []).map((p) => ({ tabId: t.id, ...p }))
      ),
    // The tab list changes on every streamed token; the holds rarely do.
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
  );
  // Pending cards are answered rather than retried: resending the last message would supersede them.
  // A turn that delivered a file is not retried either, even from a reply before an approval: the server
  // keeps no record of a failed turn, so the model would write the file again.
  readonly canRetry = computed(() => {
    const messages = this.messages();
    const lastUser = messages.map((m) => m.role).lastIndexOf('user');
    return (
      !!messages.at(-1)?.error?.retryable &&
      !this.hasPendingApprovals() &&
      !this.busyElsewhere() &&
      !messages.slice(lastUser).some((m) => m.files?.length)
    );
  });
  private readonly keys = signal<Record<string, string>>({});
  private readonly apiKeyValue = computed(() => {
    const id = this.connectionId();
    return id === null ? '' : this.keyFor(id);
  });
  readonly needsKey = computed(
    () => this.selectedConnection()?.source === 'user' && this.apiKeyValue().trim() === ''
  );
  readonly canSend = computed(
    () =>
      this.selectedConnection() !== null &&
      !!this.model() &&
      this.dataSpaceId() !== null &&
      !this.streaming() &&
      !this.needsKey() &&
      !this.busyElsewhere()
  );
  private readonly aborts = new Map<string, AbortController>();
  /** Hand-overs that ended, or tried to end, this tab's session: a retry still records them. */
  private readonly handOversStarted = new Set<string>();
  private loadedFor: number | null = null;
  private scopeKey: string | undefined;
  private scope: Scope | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    effect(() => {
      const userName = this.userName();
      const dataSpaceId = this.dataSpaceId();
      untracked(() => this.restoreTabs(userName, dataSpaceId));
    });
    effect(() => {
      this.tabList();
      this.activeId();
      untracked(() => this.scheduleSave());
    });
    effect(() => {
      const holds = this.planHolds();
      untracked(() =>
        this.store.dispatch(PlanEditActions.assistantHoldersChanged({ holds }))
      );
    });
    const flush = () => {
      this.flushSave();
      this.sync.releaseAll();
    };
    window.addEventListener('pagehide', flush);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('pagehide', flush);
      this.cancelSave();
    });
    effect(() => {
      const text = this.draft();
      untracked(() => this.sync.rememberDraft(text));
    });
    effect(() => {
      const dataSpaceId = this.dataSpaceId();
      const open = this.panelOpen();
      // Not toggleVisible: a visitor's button follows the config, so a failed load would re-run this and retry forever.
      const wanted =
        this.hasAssistantPermission() && (this.visiting() || (open && this.flagActive()));
      untracked(() => {
        if (dataSpaceId !== this.loadedFor && this.loadedFor !== null) {
          // Otherwise the old space's access and connections show until the new config arrives.
          this.config.set(null);
          this.loadedFor = null;
        }
        if (wanted && dataSpaceId !== null && dataSpaceId !== this.loadedFor)
          void this.loadConfig(dataSpaceId);
      });
    });
  }

  togglePanel(): void {
    this.panelOpen.update((open) => !open);
  }

  togglePanelSize(): void {
    this.panelSize.update((size) => (size === 'thin' ? 'large' : 'thin'));
  }

  toggleFocus(): void {
    const size = this.panelSize();
    if (size === 'focus') this.panelSize.set(this.sizeBeforeFocus);
    else {
      this.sizeBeforeFocus = size;
      this.panelSize.set('focus');
    }
  }

  openInApp(url: string): void {
    // A relative or hash link resolved in the assistant's window points back at that window's route.
    if (url.startsWith(`/${AppRoutes.assistantWindow.root}`)) return;
    if (this.link.role() === 'child') {
      this.link.navigate(url);
      return;
    }
    // In focus the chat covers the page the link opens.
    if (this.panelSize() === 'focus') this.toggleFocus();
    void this.router.navigateByUrl(url);
  }

  /** Saves now rather than after the debounce, so a window opened next reads every message. */
  saveNow(): void {
    this.flushSave();
  }

  openPreview(file: AssistantFile): void {
    if (file.content !== null) this.previewFile.set(file);
  }

  closePreview(): void {
    this.previewFile.set(null);
  }

  /** The key pasted in the panel for the provider's own-key connection, for the settings page's context review. */
  keyForProvider(provider: AssistantProviderId): string {
    return this.keyFor(`user-${provider}`).trim();
  }

  apiKey(): string {
    return this.apiKeyValue();
  }

  selectConnection(id: string): void {
    const connection = this.connections().find((c) => c.id === id);
    if (!connection || id === this.connectionId()) return;
    this.useChoice(id, connection.default_model);
  }

  selectModel(model: string): void {
    if (model === this.model()) return;
    this.useChoice(this.connectionId(), model);
  }

  newTab(): void {
    const tab = emptyTab(this.connectionId(), this.model());
    this.tabList.update((list) => [...list, tab]);
    this.activeId.set(tab.id);
  }

  selectTab(id: string): void {
    if (this.tabList().some((t) => t.id === id)) this.activeId.set(id);
  }

  closeTab(id: string): void {
    if (this.busyTabIds().has(id)) return;
    const conversationId = this.tabList().find((t) => t.id === id)?.conversationId;
    // Ending it releases any plan the assistant still holds in it.
    if (conversationId) void this.endConversation(conversationId);
    this.removeTab(id);
  }

  openChatTab(tabId: string): void {
    this.selectTab(tabId);
    this.panelOpen.set(true);
  }

  async discardPlanEdit(tabId: string, editSessionId: string): Promise<boolean> {
    const conversationId = this.tabList().find((t) => t.id === tabId)?.conversationId;
    const token = await this.freshToken();
    const outcome =
      conversationId && token
        ? await this.api.closePlanEdit(conversationId, editSessionId, token)
        : conversationId
          ? 'failed'
          : 'gone';
    if (outcome === 'failed') return false;
    this.updateTab(tabId, (t) => ({
      ...t,
      planEdits: (t.planEdits ?? []).filter((p) => p.editSessionId !== editSessionId)
    }));
    return true;
  }

  private async endConversation(conversationId: string): Promise<void> {
    const token = await this.freshToken();
    if (token) await this.api.endConversation(conversationId, token).catch(() => undefined);
  }

  private freshToken(): Promise<string | null> {
    return firstValueFrom(this.tokens.getFreshAccessToken(MIN_TOKEN_TTL_MS)).catch(
      () => null
    );
  }

  private removeTab(id: string): void {
    const list = this.tabList();
    const index = list.findIndex((t) => t.id === id);
    if (index === -1) return;
    this.aborts.get(id)?.abort();
    const rest = list.filter((t) => t.id !== id);
    if (rest.length === 0) rest.push(emptyTab(list[index].connectionId, list[index].model));
    this.tabList.set(rest);
    if (this.activeId() === id)
      this.activeId.set(rest[Math.min(index, rest.length - 1)].id);
  }

  setApiKey(key: string): void {
    const id = this.connectionId();
    if (id === null) return;
    this.keys.update((keys) => ({ ...keys, [id]: key }));
    try {
      sessionStorage.setItem(KEY_PREFIX + id, key);
    } catch {
      // Storage can be unavailable (private mode); the key then lives only in memory for this tab.
    }
  }

  setApprovalMode(mode: AssistantApprovalMode): void {
    this.updateTab(this.activeId(), (t) => ({ ...t, approvalMode: mode }));
  }

  stop(): void {
    this.aborts.get(this.activeId())?.abort();
  }

  async retry(): Promise<void> {
    const lastUser = [...this.messages()].reverse().find((m) => m.role === 'user');
    if (!lastUser || !this.canSend() || !this.canRetry()) return;
    const messages = this.messages();
    const lastTurn = messages.slice(messages.lastIndexOf(lastUser));
    // Resending would run the changes of the failed reply a second time.
    if (lastTurn.some(ranChange)) return;
    const tabId = this.activeId();
    this.updateTab(tabId, (t) => ({
      ...t,
      messages: t.messages.slice(0, t.messages.lastIndexOf(lastUser))
    }));
    await this.sendMessage(lastUser.text, lastUser.rewindTo);
  }

  // A tab with messages keeps its connection and model, so a different choice opens a new tab.
  private useChoice(connectionId: string | null, model: string | null): void {
    if (this.messages().length > 0) this.newTab();
    this.updateTab(this.activeId(), (t) => ({
      ...t,
      connectionId,
      model,
      approvalMode: 'ask',
      alwaysAllow: []
    }));
  }

  // Each user has their own tabs per data space, saved across page loads.
  private restoreTabs(
    userName: string | null,
    dataSpaceId: number | null,
    force = false
  ): void {
    const key = `${userName}|${dataSpaceId}`;
    if (key === this.scopeKey && !force) return;
    this.scopeKey = key;
    // A save for another user must not land after logout has cleared the storage, and a reload
    // after another tab cleared it must not write the stale list back.
    const keep = !force && userName !== null && userName === this.scope?.userName;
    // Saved here, not left to the save effect, which may not have seen the latest change yet.
    if (keep) this.flushSave();
    else this.cancelSave();
    this.sync.leave(keep);
    for (const abort of this.aborts.values()) abort.abort();
    // The preview lives in the app shell, outside the panel, so it would otherwise outlive its scope and
    // show one user's report to the next.
    this.previewFile.set(null);
    this.scope =
      userName !== null && dataSpaceId !== null ? { userName, dataSpaceId } : null;
    const scope = this.scope;
    const restored = this.sync.open(scope, {
      tabs: () => this.tabList(),
      changed: (change) => this.applyRemote(change),
      // Dropped in memory too, or every later save would serialise those contents again before making room.
      dropped: (fileIds) =>
        this.tabList.update((list) => withoutFileContents(list, fileIds)),
      valid: () => scope !== null && this.userName() === scope.userName
    });
    const config = this.config();
    const fit = (t: AssistantTab) =>
      config && this.loadedFor === dataSpaceId ? fitTab(t, config.connections) : t;
    const tabs = restored?.tabs.map(fit) ?? [emptyTab(this.connectionId(), this.model())];
    this.tabList.set(tabs);
    this.activeId.set(restored?.activeId ?? tabs[0].id);
    this.draft.set(this.sync.draft());
    for (const run of tabs.flatMap((t) => t.messages.flatMap((m) => m.runs ?? [])))
      this.runs.follow(run.optimizationId);
  }

  private applyRemote(change: SyncChange): void {
    switch (change.kind) {
      case 'reload':
        this.restoreTabs(this.userName(), this.dataSpaceId(), true);
        return;
      case 'upsert': {
        const tab = change.conversation.tab;
        this.tabList.update((list) =>
          list.some((t) => t.id === tab.id)
            ? list.map((t) => (t.id === tab.id ? tab : t))
            : [...list, tab]
        );
        for (const run of tab.messages.flatMap((m) => m.runs ?? []))
          this.runs.follow(run.optimizationId);
        return;
      }
      case 'delete':
        this.removeTab(change.id);
        return;
      case 'expired':
        this.updateTab(change.id, interruptStreaming);
        return;
    }
  }

  private scheduleSave(): void {
    if (this.scope) this.saveTimer ??= setTimeout(() => this.flushSave(), SAVE_DELAY_MS);
  }

  // The tabs always belong to the current scope: both are replaced together in restoreTabs.
  private flushSave(): void {
    this.cancelSave();
    if (this.scope) this.sync.save(this.tabList(), this.activeId());
  }

  private cancelSave(): void {
    clearTimeout(this.saveTimer);
    this.saveTimer = undefined;
  }

  private updateTab(id: string, fn: (t: AssistantTab) => AssistantTab): void {
    this.tabList.update((list) => list.map((t) => (t.id === id ? fn(t) : t)));
  }

  async send(text: string): Promise<void> {
    await this.sendMessage(text);
  }

  // Each message asks on every render, so the answer for the whole transcript is worked out once.
  private readonly editableIds = computed(() => {
    const ids = new Set<string>();
    if (!this.canSend()) return ids;
    const messages = this.messages();
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      // A change cannot be undone, and before a notice the server no longer holds the messages.
      if (m.role === 'notice' || ranChange(m)) break;
      if (m.role === 'user' && m.turnId) ids.add(m.id);
    }
    return ids;
  });

  canEdit(messageId: string): boolean {
    return this.editableIds().has(messageId);
  }

  async editAndResend(messageId: string, text: string): Promise<void> {
    const turnId = this.messages().find((m) => m.id === messageId)?.turnId;
    if (!turnId || !text.trim() || !this.canEdit(messageId)) return;
    this.updateTab(this.activeId(), (t) => ({
      ...t,
      messages: t.messages.slice(
        0,
        t.messages.findIndex((m) => m.id === messageId)
      )
    }));
    await this.sendMessage(text, turnId);
  }

  async copyText(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  private async sendMessage(text: string, rewindTo?: string): Promise<void> {
    const connection = this.selectedConnection();
    const model = this.model();
    const dataSpaceId = this.dataSpaceId();
    const message = text.trim();
    const summary = this.contextSummary();
    if (!connection || !model || dataSpaceId === null || !message || !this.canSend())
      return;

    const tabId = this.activeId();
    // None of these answers was sent, and the server declines every call of the turn.
    this.updateTab(tabId, (t) => ({
      ...t,
      messages: t.messages.map((m) =>
        m.approvals?.some((c) => c.state === 'pending')
          ? { ...m, approvals: m.approvals.map((c) => ({ ...c, state: 'superseded' })) }
          : m
      )
    }));
    const body: AssistantChatRequest = {
      conversation_id: this.activeTab().conversationId,
      data_space_id: dataSpaceId,
      connection_id: connection.id,
      model,
      message,
      approval: approvalSettings(this.activeTab()),
      page_context: this.pageContext()!,
      ...this.editHolds(),
      ...this.lang(),
      ...(rewindTo ? { rewind_to: rewindTo } : {}),
      ...(connection.source === 'user' ? { api_key: this.apiKeyValue().trim() } : {})
    };
    const userId = crypto.randomUUID();
    const replyId = crypto.randomUUID();
    this.updateTab(tabId, (t) => ({
      ...t,
      streaming: true,
      messages: [
        ...t.messages,
        {
          id: userId,
          role: 'user',
          text: message,
          tools: [],
          status: 'done',
          ...(rewindTo ? { rewindTo } : {}),
          ...(summary ? { context: summary } : {})
        },
        { id: replyId, role: 'assistant', text: '', tools: [], status: 'streaming' }
      ]
    }));
    const scope = this.scope;
    if (!(await this.sync.claim(tabId))) {
      // Another tab started a reply here in the same moment; this message was never sent.
      this.updateTab(tabId, (t) => ({
        ...t,
        streaming: false,
        messages: t.messages.filter((m) => m.id !== userId && m.id !== replyId)
      }));
      // The draft now on screen belongs to whichever scope replaced this one during the claim.
      if (this.scope === scope) this.draft.set(message);
      return;
    }
    await this.run(tabId, replyId, body);
  }

  answerApproval(
    messageId: string,
    approvalId: string,
    approved: boolean,
    alwaysAllow = false
  ): void {
    if (this.canSend())
      this.answerOn(this.activeId(), messageId, approvalId, approved, alwaysAllow);
  }

  private answerOn(
    tabId: string,
    messageId: string,
    approvalId: string,
    approved: boolean,
    alwaysAllow = false
  ): void {
    const tab = this.tabList().find((t) => t.id === tabId);
    const card = tab?.messages
      .find((m) => m.id === messageId)
      ?.approvals?.find((c) => c.approvalId === approvalId);
    if (
      !tab ||
      !card ||
      card.state !== 'pending' ||
      tab.streaming ||
      this.busyTabIds().has(tabId) ||
      tab.conversationId === null
    )
      return;
    this.updateTab(tab.id, (t) => ({
      ...t,
      alwaysAllow:
        alwaysAllow && approved && !t.alwaysAllow.includes(card.name)
          ? [...t.alwaysAllow, card.name]
          : t.alwaysAllow,
      messages: t.messages.map((m) =>
        m.id !== messageId
          ? m
          : {
              ...m,
              approvals: m.approvals?.map((c) =>
                c.approvalId === approvalId
                  ? { ...c, state: approved ? 'approved' : 'rejected' }
                  : c
              )
            }
      )
    }));
    void this.resumeIfAnswered(tab.id, messageId);
  }

  answerAllApprovals(messageId: string, approved: boolean): void {
    const pending =
      this.activeTab()
        .messages.find((m) => m.id === messageId)
        ?.approvals?.filter((c) => c.state === 'pending') ?? [];
    // Approving a hand-over must end this tab's edit session first, which only its own buttons do.
    for (const card of pending)
      if (!approved || card.kind !== 'handover')
        this.answerApproval(messageId, card.approvalId, approved);
  }

  /**
   * Approves a hand-over once this tab's edit session on the plan has ended, so the assistant's open
   * finds the plan free. A refused save or a lost session leaves the card waiting: resolves false.
   */
  async handOver(
    messageId: string,
    approvalId: string,
    end: 'save' | 'discard'
  ): Promise<boolean> {
    // The conversation that asked, which need not be the one open when the session has closed.
    const tabId = this.activeId();
    const card = this.activeTab()
      .messages.find((m) => m.id === messageId)
      ?.approvals?.find((c) => c.approvalId === approvalId);
    const planId = card?.arguments['plan_id'];
    if (card?.state !== 'pending' || card.kind !== 'handover' || typeof planId !== 'string')
      return false;
    const { mode, planId: editedPlanId } = this.editSession();
    if (mode !== 'off' && editedPlanId === planId) {
      this.handOversStarted.add(approvalId);
      // Only an open session can save or close; a save already in flight is waited for.
      if (mode !== 'editing' && mode !== 'saving') return false;
      if (end === 'save') {
        // Subscribed before the dispatch: the effect answers a save the socket cannot send synchronously.
        const saved = firstValueFrom(
          this.actions$.pipe(
            ofType(
              Session.saved,
              Session.saveRejected,
              Session.closed,
              Session.connectionLost
            ),
            map((action) => action.type === Session.saved.type)
          )
        );
        this.store.dispatch(Session.saveRequested());
        if (!(await saved)) return false;
      }
      const closed = firstValueFrom(
        this.actions$.pipe(
          ofType(Session.closed, Session.connectionLost),
          map((action) => action.type === Session.closed.type)
        )
      );
      this.store.dispatch(
        end === 'save' ? Session.doneRequested() : Session.discardRequested()
      );
      // Effects see an action after the reducers: a close DL parks for a resume keeps the session,
      // and the plan, with this tab.
      if (!(await closed) || this.editSession().mode !== 'off') return false;
    }
    if (this.handOversStarted.has(approvalId))
      this.store.dispatch(Session.handedOver({ planId }));
    this.answerOn(tabId, messageId, approvalId, true);
    return true;
  }

  /**
   * A hand-over whose resume did not open the plan is forgotten: a later release of that plan, from an
   * open the planner never handed over, must not reopen edit mode.
   */
  private forgetUntakenHandOvers(tabId: string, cards: AssistantApprovalCard[]): void {
    const held = this.tabList().find((t) => t.id === tabId)?.planEdits ?? [];
    const handedOver = this.editSession().handedOverPlanIds;
    for (const card of cards) {
      const planId = card.arguments['plan_id'];
      if (
        card.kind === 'handover' &&
        typeof planId === 'string' &&
        handedOver.includes(planId) &&
        !held.some((p) => p.planId === planId)
      )
        this.store.dispatch(Session.handedBack({ planId }));
    }
  }

  private lang(): Pick<AssistantChatRequest, 'lang'> {
    const lang = this.translate.getCurrentLang();
    return lang && LANG_TAG.test(lang) ? { lang } : {};
  }

  private editHolds(): Pick<AssistantChatRequest, 'edit_holds'> {
    const { mode, planId } = this.editSession();
    return mode !== 'off' && planId !== null ? { edit_holds: [{ plan_id: planId }] } : {};
  }

  /** The planner's session ended to let the assistant in, so it reopens once the plan is free. */
  private handBack(planId: string): void {
    if (!this.editSession().handedOverPlanIds.includes(planId)) return;
    this.store.dispatch(Session.handedBack({ planId }));
    const plan = this.plan();
    const paramId = plan?.parameter?.id;
    // Edit mode opens on the plan on screen, with its own parameter.
    if (plan?.id !== planId || paramId === undefined || paramId === null) return;
    this.store.dispatch(PlanActions.fetchPlans({ id: planId }));
    this.store.dispatch(PlanEditActions.planDataReplaced({ planId }));
    this.store.dispatch(Session.editRequested({ planId, paramId }));
  }

  private async resumeIfAnswered(tabId: string, messageId: string): Promise<void> {
    const tab = this.tabList().find((t) => t.id === tabId);
    const cards = tab?.messages.find((m) => m.id === messageId)?.approvals ?? [];
    if (!tab || cards.length === 0 || cards.some((c) => c.state === 'pending')) return;
    const connection = this.connections().find((c) => c.id === tab.connectionId);
    const dataSpaceId = this.dataSpaceId();
    if (!connection || !tab.model || dataSpaceId === null || tab.conversationId === null)
      return;
    const body: AssistantChatRequest = {
      conversation_id: tab.conversationId,
      data_space_id: dataSpaceId,
      connection_id: connection.id,
      model: tab.model,
      approvals: cards.map((c) => ({
        approval_id: c.approvalId,
        approved: c.state === 'approved'
      })),
      approval: approvalSettings(tab),
      ...this.editHolds(),
      ...this.lang(),
      ...(connection.source === 'user'
        ? { api_key: this.keyFor(connection.id).trim() }
        : {})
    };
    if (!(await this.sync.claim(tabId))) {
      this.setCardStates(tabId, ['approved', 'rejected'], 'pending', messageId);
      return;
    }
    const replyId = crypto.randomUUID();
    this.updateTab(tabId, (t) => ({
      ...t,
      streaming: true,
      messages: [
        ...t.messages,
        { id: replyId, role: 'assistant', text: '', tools: [], status: 'streaming' }
      ]
    }));
    const kept = await this.run(tabId, replyId, body);
    this.forgetUntakenHandOvers(tabId, cards);
    const reply = this.tabList()
      .find((t) => t.id === tabId)
      ?.messages.find((m) => m.id === replyId);
    if (!reply || !endedEarly(reply)) return;
    if (kept) {
      this.setCardStates(tabId, ['approved', 'rejected'], 'pending', messageId);
      return;
    }
    // The server discards a conversation whose resume failed, because approved calls may already have run.
    const expired = reply.error?.code === 'conversation_not_found';
    // A declined call never runs, so only an approved one has an uncertain outcome.
    this.setCardStates(tabId, ['approved'], expired ? 'expired' : 'unknown', messageId);
    // Cards the failed resume streamed can never be answered: the conversation is gone.
    this.setCardStates(tabId, ['pending'], expired ? 'expired' : 'unknown', replyId);
    if (!expired) this.resetConversation(tabId);
  }

  /** Resolves true when the server left the conversation untouched, or the request was never sent. */
  private async run(
    tabId: string,
    replyId: string,
    body: AssistantChatRequest
  ): Promise<boolean> {
    const abort = new AbortController();
    this.aborts.set(tabId, abort);
    const reply = { tabId, replyId };
    try {
      let request = body;
      let firstAttempt = true;
      for (;;) {
        const outcome = await this.stream(request, reply, abort, firstAttempt);
        if (outcome === 'done' || outcome === 'kept') return outcome === 'kept';
        if (outcome === 'unauthenticated') firstAttempt = false;
        else {
          this.restartConversationBefore(tabId, replyId);
          // A new conversation has nothing to rewind.
          request = { ...request, conversation_id: null, rewind_to: undefined };
        }
      }
    } finally {
      this.updateTab(tabId, (t) => ({ ...t, streaming: false }));
      this.sync.release(tabId);
      if (this.aborts.get(tabId) === abort) this.aborts.delete(tabId);
    }
  }

  private setCardStates(
    tabId: string,
    from: AssistantApprovalState[],
    to: AssistantApprovalState,
    messageId?: string
  ): void {
    this.updateTab(tabId, (t) => ({
      ...t,
      messages: t.messages.map((m) =>
        (messageId === undefined || m.id === messageId) && m.approvals
          ? {
              ...m,
              approvals: m.approvals.map((c) =>
                from.includes(c.state) ? { ...c, state: to } : c
              )
            }
          : m
      )
    }));
  }

  // The notice tells the user that the messages above it are no longer in the assistant's context.
  private resetConversation(tabId: string): void {
    this.updateTab(tabId, (t) =>
      t.conversationId === null && t.messages.at(-1)?.role === 'notice'
        ? t
        : {
            ...t,
            conversationId: null,
            planEdits: [],
            contextNotice: undefined,
            messages: [
              ...t.messages,
              {
                id: crypto.randomUUID(),
                role: 'notice',
                text: '',
                tools: [],
                status: 'done'
              }
            ]
          }
    );
  }

  // A stopped reply never hears whether the server discarded its conversation; the next message finds out.
  // Nothing ran on the lost conversation, so the message is resent on a new one, after the notice.
  private restartConversationBefore(tabId: string, replyId: string): void {
    this.updateTab(tabId, (t) => {
      const at = t.messages.findIndex((m) => m.id === replyId) - 1;
      const notice: AssistantMessage = {
        id: crypto.randomUUID(),
        role: 'notice',
        text: '',
        tools: [],
        status: 'done'
      };
      return {
        ...t,
        conversationId: null,
        planEdits: [],
        contextNotice: undefined,
        messages: [...t.messages.slice(0, at), notice, ...t.messages.slice(at)]
      };
    });
  }

  private approvedCallIds(tabId: string): Set<string> {
    const tab = this.tabList().find((t) => t.id === tabId);
    return new Set(
      tab?.messages
        .flatMap((m) => m.approvals ?? [])
        .filter((c) => c.state === 'approved')
        .map((c) => c.callId)
    );
  }

  private keyFor(id: string): string {
    return this.keys()[id] ?? this.readKey(id);
  }

  private async stream(
    body: AssistantChatRequest,
    reply: Reply,
    abort: AbortController,
    firstAttempt: boolean
  ): Promise<Outcome> {
    // After the server refused a token, only a newly issued one can change its answer.
    const token$ = firstAttempt
      ? this.tokens.getFreshAccessToken(MIN_TOKEN_TTL_MS)
      : this.tokens.refreshAccessToken();
    const token = await firstValueFrom(token$).catch(() => null);
    if (!token) {
      this.patch(reply, (m) => ({ ...m, status: 'error', error: NO_TOKEN }));
      return 'kept';
    }
    let kept = false;
    try {
      for await (const event of this.api.chat(body, token, abort.signal)) {
        if (
          firstAttempt &&
          event.event === 'error' &&
          event.data.code === 'unauthenticated'
        )
          return 'unauthenticated';
        if (
          body.message !== undefined &&
          body.conversation_id !== null &&
          event.event === 'error' &&
          event.data.code === 'conversation_not_found'
        )
          return 'conversation_lost';
        if (event.event === 'error' && !event.data.conversation_discarded) kept = true;
        this.apply(reply, event);
      }
      // The server always closes a turn with turn.end or error, so a stream that just stops was cut off.
      this.patch(reply, (m) =>
        m.status === 'streaming'
          ? { ...m, status: 'error', error: NETWORK, tools: cancelTools(m) }
          : m
      );
    } catch {
      if (abort.signal.aborted)
        this.patch(reply, (m) => ({ ...m, status: 'cancelled', tools: cancelTools(m) }));
      else
        this.patch(reply, (m) => ({
          ...m,
          status: 'error',
          error: NETWORK,
          tools: cancelTools(m)
        }));
      return 'done';
    }
    return kept ? 'kept' : 'done';
  }

  private apply(reply: Reply, event: AssistantEvent): void {
    switch (event.event) {
      case 'turn.start':
        this.updateTab(reply.tabId, (t) => {
          const asked = t.messages.findIndex((m) => m.id === reply.replyId) - 1;
          return {
            ...t,
            conversationId: event.data.conversation_id,
            messages: t.messages.map((m, i) =>
              i === asked && m.role === 'user' ? startedTurn(m, event.data.turn_id) : m
            )
          };
        });
        return;
      case 'text.delta':
        this.patch(reply, (m) => ({ ...m, text: m.text + event.data.text }));
        return;
      case 'tool.start': {
        const access = event.data.access ?? 'read';
        const approval = this.approvedCallIds(reply.tabId).has(event.data.call_id)
          ? 'user'
          : 'auto';
        this.patch(reply, (m) => ({
          ...m,
          tools: [
            ...m.tools,
            {
              callId: event.data.call_id,
              name: event.data.name,
              arguments: event.data.arguments ?? {},
              access,
              status: 'running',
              ...(access === 'read' ? {} : { approval })
            }
          ]
        }));
        return;
      }
      case 'tool.end':
        this.patch(reply, (m) => ({
          ...m,
          tools: m.tools.map((t) =>
            t.callId === event.data.call_id
              ? {
                  ...t,
                  status: event.data.ok ? 'ok' : 'failed',
                  durationMs: event.data.duration_ms
                }
              : t
          )
        }));
        return;
      case 'turn.end':
        this.patch(reply, (m) => ({
          ...m,
          status: event.data.stop === 'cancelled' ? 'cancelled' : 'done',
          stop: event.data.stop,
          tools: cancelTools(m)
        }));
        if (event.data.conversation_discarded) this.resetConversation(reply.tabId);
        return;
      case 'error': {
        const { conversation_discarded, ...error } = event.data;
        this.patch(reply, (m) => ({
          ...m,
          status: 'error',
          error,
          tools: cancelTools(m)
        }));
        if (conversation_discarded) this.resetConversation(reply.tabId);
        return;
      }
      case 'tool.approval': {
        const editSessionId = event.data.arguments?.['edit_session_id'];
        const planId = this.tabList()
          .find((t) => t.id === reply.tabId)
          ?.planEdits?.find((p) => p.editSessionId === editSessionId)?.planId;
        this.patch(reply, (m) => ({
          ...m,
          approvals: [
            ...(m.approvals ?? []),
            {
              approvalId: event.data.approval_id,
              callId: event.data.call_id,
              name: event.data.name,
              arguments: event.data.arguments ?? {},
              access: event.data.access,
              state: 'pending',
              ...(planId ? { planId } : {}),
              ...(event.data.kind === 'handover' ? { kind: 'handover' as const } : {})
            }
          ]
        }));
        return;
      }
      case 'optimization.started': {
        const optimizationId = event.data.optimization_id;
        this.runs.track(optimizationId);
        this.patch(reply, (m) => ({
          ...m,
          runs: [
            ...(m.runs ?? []),
            {
              optimizationId,
              callId: event.data.call_id,
              planId: event.data.plan_id,
              planningSpaceId: null,
              startedAt: Date.now()
            }
          ]
        }));
        // The plan the assistant ran need not be in the planning space of the page that is open.
        // The card works without it, so a plan deleted or out of reach since the run raises no toast.
        this.plans
          .fetchOne$({ planId: event.data.plan_id }, { skipErrorNotification: true })
          .subscribe({
            next: (plan) =>
              this.patch(reply, (m) => ({
                ...m,
                runs: m.runs?.map((r) =>
                  r.optimizationId === optimizationId
                    ? { ...r, planningSpaceId: plan.planningSpace.id }
                    : r
                )
              })),
            error: () => undefined
          });
        return;
      }
      case 'plan_edit.state': {
        const edit: AssistantPlanEdit = {
          editSessionId: event.data.edit_session_id,
          planId: event.data.plan_id,
          dataSpaceId: event.data.data_space_id
        };
        if (event.data.state === 'saved') {
          if (event.data.plan_data_revision)
            this.store.dispatch(
              PlanEditActions.planSaved({
                planId: edit.planId,
                planDataRevision: event.data.plan_data_revision
              })
            );
          return;
        }
        this.updateTab(reply.tabId, (t) => {
          const others = (t.planEdits ?? []).filter(
            (p) => p.editSessionId !== edit.editSessionId
          );
          return {
            ...t,
            planEdits: event.data.state === 'held' ? [...others, edit] : others
          };
        });
        if (event.data.state === 'released') this.handBack(edit.planId);
        return;
      }
      case 'file.created':
        this.patch(reply, (m) => ({
          ...m,
          files: [
            ...(m.files ?? []),
            {
              fileId: event.data.file_id,
              callId: event.data.call_id,
              filename: event.data.filename,
              title: event.data.title,
              format: event.data.format,
              content: event.data.content,
              createdAt: Date.now(),
              summary: event.data.summary,
              subject: event.data.subject,
              meta: event.data.meta,
              comparedTo: event.data.compared_to
            }
          ]
        }));
        return;
      case 'warning': {
        const notice = CONTEXT_NOTICES.get(event.data.code);
        if (notice) this.updateTab(reply.tabId, (t) => ({ ...t, contextNotice: notice }));
        return;
      }
    }
  }

  private patch(reply: Reply, fn: (m: AssistantMessage) => AssistantMessage): void {
    this.updateTab(reply.tabId, (t) => ({
      ...t,
      messages: t.messages.map((m) => (m.id === reply.replyId ? fn(m) : m))
    }));
  }

  private async loadConfig(dataSpaceId: number): Promise<void> {
    this.loadedFor = dataSpaceId;
    this.configError.set(null);
    try {
      const token = await this.freshToken();
      if (!token) throw new AssistantConfigError('unauthenticated');
      const config = await this.api.getConfig(dataSpaceId, token);
      if (this.loadedFor !== dataSpaceId) return;
      this.config.set(config);
      this.tabList.update((list) => list.map((t) => fitTab(t, config.connections)));
    } catch (err) {
      if (this.loadedFor === dataSpaceId) {
        this.config.set(null);
        this.configError.set(err instanceof AssistantConfigError ? err.code : 'internal');
        this.loadedFor = null;
      }
    }
  }

  private readKey(connectionId: string): string {
    try {
      return sessionStorage.getItem(KEY_PREFIX + connectionId) ?? '';
    } catch {
      return '';
    }
  }
}

// The server has taken the turn up, so a pending rewind is done.
function startedTurn(m: AssistantMessage, turnId: string): AssistantMessage {
  const { rewindTo: _rewindTo, ...rest } = m;
  return { ...rest, turnId };
}

function approvalSettings(
  tab: AssistantTab
): NonNullable<AssistantChatRequest['approval']> {
  return { mode: tab.approvalMode, always_allow: tab.alwaysAllow };
}

// A refused turn is not kept in the conversation either.
function endedEarly(m: AssistantMessage): boolean {
  return m.status === 'error' || m.status === 'cancelled' || m.stop === 'refusal';
}

function ranChange(m: AssistantMessage): boolean {
  return m.tools.some((t) => t.access !== 'read');
}

function cancelTools(m: AssistantMessage): AssistantMessage['tools'] {
  return m.tools.map((t) => (t.status === 'running' ? { ...t, status: 'cancelled' } : t));
}

function emptyTab(connectionId: string | null, model: string | null): AssistantTab {
  return {
    id: crypto.randomUUID(),
    connectionId,
    model,
    conversationId: null,
    messages: [],
    streaming: false,
    approvalMode: 'ask',
    alwaysAllow: []
  };
}

// A tab keeps its choice when the new config still offers it, and falls back to the defaults otherwise.
function fitTab(tab: AssistantTab, connections: AssistantConnection[]): AssistantTab {
  const current = connections.find((c) => c.id === tab.connectionId);
  const connection = current ?? connections[0];
  if (!connection) return tab;
  const model =
    current && current.models.includes(tab.model ?? '')
      ? tab.model
      : connection.default_model;
  if (connection.id === tab.connectionId && model === tab.model) return tab;
  return { ...tab, connectionId: connection.id, model };
}
